import svgLogo from '../assets/logo.png'
import { motion } from 'framer-motion'
import { useSound } from '../components/SoundHandler'
import { ReactNode, useEffect, useState } from 'react'
import { Achievement, ACHIEVEMENTS } from '../assets/new-achievements/achievements'
import { useMetadata } from '../components/MetadataHandler'
import { useResolution } from '../hooks/useResolution'
import { icons } from '../assets/icons/icons'
import useSWR from 'swr'
import { ContainerShape, HexagonShape, InnerShape } from '../components/Shape'

const useResetTimer = () => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date()
      const nextReset = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0, 0, 0, 0
      ))
      const diff = nextReset.getTime() - now.getTime()

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

      return `${hours}h ${minutes}m`
    }

    setTimeLeft(calculateTimeLeft())
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  return timeLeft
}

export const NextAchievementSlot = ({ children }: { children: (achievement: Achievement & { progress: number }) => ReactNode }) => {
  const { achievementsMetadata } = useMetadata()

  if (!achievementsMetadata) {
    return null
  }

  const nextAchievement = ACHIEVEMENTS.find((achievement) => achievement.getProgress(achievementsMetadata) < 1) ?? ACHIEVEMENTS[ACHIEVEMENTS.length - 1]

  return children({
    ...nextAchievement,
    progress: nextAchievement.getProgress(achievementsMetadata),
  })
}

type AchievementCategory = 'All' | 'Bonus' | 'Streak' | 'Honey' | 'Multiplier' | 'Referrals'

export const AchievementsModal = ({ onDismiss }: { onDismiss: () => void }) => {
  const sound = useSound();
  const { achievementsMetadata, mutateAchievements } = useMetadata()
  const { isMobile } = useResolution()
  const resetTimer = useResetTimer()

  const [activeTab, setActiveTab] = useState<AchievementCategory>('All')

  useSWR('moonsheep-cannon-achievements', mutateAchievements)

  const onClose = () => {
    onDismiss()
    sound.play('click');
  }

  const onSetActiveTab = (tab: AchievementCategory) => {
    setActiveTab(tab)
    sound.play('click')
  }
  
  const filteredAchievements = ACHIEVEMENTS.filter((achievement) => {
    if (activeTab === 'All') return true
    return achievement.category === activeTab
  })

  const completedAchievements: Achievement[] = []
  const incompleteAchievements: Achievement[] = []
  filteredAchievements.forEach((achievement) => {
    if (achievementsMetadata && achievement.getProgress(achievementsMetadata) >= 1) {
      completedAchievements.push(achievement)
    } else {
      incompleteAchievements.push(achievement)
    }
  })

  const ui = {
    completed: `Unlocked: ${completedAchievements.length}/${filteredAchievements.length}`,
  }

  return (
    <div className="fixed z-30 inset-0">
      <motion.div
        className="absolute inset-0 bg-[#0a0523]/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />

      <motion.div
        className="absolute inset-0"
        initial={{ backdropFilter: 'blur(20px) opacity(0)' }}
        animate={{ backdropFilter: 'blur(20px) opacity(1)' }}
      />

      <div className='flex flex-col gap-4 md:gap-[45px] px-2 md:px-0 pt-6 pb-4 md:py-10 max-w-[1280px] mx-auto h-full'>
        {isMobile ? (
          <div className="relative">
            <motion.div
              className="flex flex-col items-center justify-center gap-1 py-2.5"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <p className="text-[24px] font-blinko text-center text-white">
                Achievement
              </p>
            </motion.div>
            
            <button className='absolute top-0 right-2' onClick={onClose}>
              <HexagonShape cornerRadius={6} strokeWidth={1}>
                <div className="flex justify-center items-center p-2">
                  <img src={icons.close} className='w-6 h-6' />
                </div>
              </HexagonShape>
            </button>
          </div>
        ) : (
          <div className="relative">
            <motion.div
              className="flex flex-col items-center justify-center gap-8"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <p className="text-[48px] font-blinko text-center text-white">
                Achievement
              </p>
            </motion.div>
            
            <button className='absolute top-0 left-0' onClick={onClose}>
              <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                <div className="px-8 py-3">
                  <p className="text-base font-blinko text-white">
                    Back to Game
                  </p>
                </div>
              </InnerShape>
            </button>
          </div>
        )}

        <div className='flex-1 overflow-hidden'>
          <ContainerShape {...(
              isMobile 
                ? {cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 } 
                : {cornerDiagonal: 60, cornerRadius: 25, strokeWidth: 2 })
          }>
            <div className='flex flex-col h-full py-6 px-3 md:px-12 md:py-12 overflow-auto'>
              <div className='w-full flex-shrink-0 overflow-hidden'>
                <div className="flex pr-10 overflow-x-auto whitespace-nowrap">
                    <TabButton active={activeTab === 'All'} onClick={() => onSetActiveTab('All')}>All</TabButton>
                    <TabButton active={activeTab === 'Bonus'} onClick={() => onSetActiveTab('Bonus')}>Bonus</TabButton>
                    <TabButton active={activeTab === 'Streak'} onClick={() => onSetActiveTab('Streak')}>Streak</TabButton>
                    <TabButton active={activeTab === 'Honey'} onClick={() => onSetActiveTab('Honey')}>Honey</TabButton>
                    <TabButton active={activeTab === 'Multiplier'} onClick={() => onSetActiveTab('Multiplier')}>Multiplier</TabButton>
                    <TabButton active={activeTab === 'Referrals'} onClick={() => onSetActiveTab('Referrals')}>Referrals</TabButton>
                </div>

                {/* <NextAchievementSlot>
                  {({ progress, ...achievement }) => (
                    <div className="flex items-center gap-3 w-full md:w-[319px]">
                      <ButtonShape border={1} borderRadius={10}>
                        <div className='w-10 h-10 p-1'>
                          <img src={achievement.image} />
                        </div>
                      </ButtonShape>
                      <div className="flex-1 flex flex-col gap-1 mr-2">
                        <div className='flex items-center'>
                          <p className="text-xs font-medium text-left">
                            <span className="text-xs font-medium text-white/70">
                              Next:{" "}
                            </span>
                            <span className="text-xs font-medium text-white">
                              {achievement.title}
                            </span>
                          </p>
                          <div className='ml-auto'>
                            <ButtonShape border={1} borderRadius={100}>
                              <div className='px-2 py-0.5'>
                                <p className="text-xs text-white">{achievement.description}</p>
                              </div>
                            </ButtonShape>
                          </div>
                        </div>
                        <div className="h-4 overflow-hidden rounded-[100px] bg-white/10 p-0.5">
                          <div
                            className="h-full rounded-[100px] bg-gradient-to-r from-indigo-300 to-[#b3bef6]"
                            style={{ 
                              width: `${Math.min(progress * 100, 100)}%`,
                              boxShadow: "0px 1px 4px 0 #5f75de"
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </NextAchievementSlot> */}
              </div>

              <div className='pt-6'>
                <div className="flex flex-wrap justify-between items-center gap-3 md:gap-4 mb-3">
                  <p className="text-sm text-white">
                    {ui.completed}
                  </p>
                  {achievementsMetadata && (
                    <div className="flex items-center gap-1.5">
                      <div className="rounded" style={{ border: '1px solid #FFF085'}}>
                        <div className="px-2 py-0.5 bg-[#F0B10080] flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-white">
                            {achievementsMetadata.currentDailyStreak} day streak
                          </span>
                          <span className="text-xs text-white/60">
                            (best: {achievementsMetadata.maxDailyStreak})
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 text-xs text-white/50">
                        <svg className="w-3 h-3" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 4.66675V7.00008L8.45833 8.45841" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M3.26908 3.26908L2.73875 2.73875L2.73875 2.73875L3.26908 3.26908ZM2.53036 4.0078L1.78037 4.01157C1.78244 4.42284 2.11532 4.75573 2.52659 4.75779L2.53036 4.0078ZM4.0115 4.76526C4.4257 4.76734 4.76317 4.43324 4.76526 4.01903C4.76734 3.60483 4.43324 3.26736 4.01903 3.26528L4.01527 4.01527L4.0115 4.76526ZM3.27289 2.51913C3.27081 2.10492 2.93334 1.77083 2.51913 1.77291C2.10492 1.77499 1.77083 2.11246 1.77291 2.52667L2.5229 2.5229L3.27289 2.51913ZM2.54179 6.33364C2.59804 5.92326 2.31097 5.54499 1.90059 5.48874C1.49021 5.43249 1.11194 5.71956 1.05569 6.12994L1.79874 6.23179L2.54179 6.33364ZM10.6936 3.30639L11.2239 2.77606C8.87555 0.427677 5.07658 0.400922 2.73875 2.73875L3.26908 3.26908L3.79941 3.79941C5.54143 2.05739 8.3906 2.06405 10.1633 3.83672L10.6936 3.30639ZM3.30639 10.6936L2.77606 11.2239C5.12445 13.5723 8.92342 13.5991 11.2612 11.2612L10.7309 10.7309L10.2006 10.2006C8.45857 11.9426 5.6094 11.936 3.83672 10.1633L3.30639 10.6936ZM10.7309 10.7309L11.2612 11.2612C13.5991 8.92342 13.5723 5.12445 11.2239 2.77606L10.6936 3.30639L10.1633 3.83672C11.936 5.6094 11.9426 8.45857 10.2006 10.2006L10.7309 10.7309ZM3.26908 3.26908L2.73875 2.73875L2.00003 3.47747L2.53036 4.0078L3.06069 4.53813L3.79941 3.79941L3.26908 3.26908ZM2.53036 4.0078L2.52659 4.75779L4.0115 4.76526L4.01527 4.01527L4.01903 3.26528L2.53413 3.25781L2.53036 4.0078ZM2.53036 4.0078L3.28035 4.00403L3.27289 2.51913L2.5229 2.5229L1.77291 2.52667L1.78037 4.01157L2.53036 4.0078ZM1.79874 6.23179L1.05569 6.12994C0.808715 7.93175 1.38397 9.83184 2.77606 11.2239L3.30639 10.6936L3.83672 10.1633C2.7879 9.11446 2.35628 7.68707 2.54179 6.33364L1.79874 6.23179Z" fill="currentColor" />
                        </svg>
                        <span>{resetTimer}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className='grid grid-cols-2 md:grid-cols-[repeat(auto-fill,minmax(176px,1fr))] justify-center gap-2 md:gap-6'>
                  {completedAchievements.map((achievement) => (
                    <Card key={achievement.title} achievement={achievement} />
                  ))}
                  {incompleteAchievements.map((achievement) => (
                    <LockedCard key={achievement.title} achievement={achievement} />
                  ))}
                </div>
              </div>
            </div>
          </ContainerShape>
        </div>
      </div>
    </div>
  )
}

const TabButton = ({ children, active, onClick }: { children: ReactNode, active: boolean, onClick: () => void }) => {
  if (active) {
    return (
      <button onClick={onClick}>
        <InnerShape cornerDiagonal={12} cornerRadius={6} strokeWidth={1}>
          <div className="px-4 py-2">
            <p className="text-sm text-white font-blinko">{children}</p>
          </div>
        </InnerShape>
      </button>
    )
  }

  return (
    <button className="px-4 py-2 rounded-xl" onClick={onClick}>
      <p className="text-sm text-white/80 font-blinko">{children}</p>
    </button>
  )
}

const Card = ({ achievement }: { achievement: Achievement }) => {
  return (
    <ContainerShape {...{ cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 }}>
      <div className="p-3 md:p-4 flex flex-col gap-2.5">
        <InnerShape {...{ cornerDiagonal: 14, cornerRadius: 6, strokeWidth: 1 }}>
          <div className="h-[120px] md:h-[162px] rounded-xl bg-[#FEBC5C] flex items-center justify-center">
            <img src={achievement.image} className="h-18 object-contain px-4" />
          </div>
        </InnerShape>

        <div className='flex flex-col items-start gap-1'>
          <div className="rounded" style={{ border: '1px solid #FFF085'}}>
            <div className="px-2 py-0.5 bg-[#F0B10080]">
              <p className="text-xs font-semibold text-white">
                {achievement.category}
              </p>
            </div>
          </div>
          <p className="text-sm font-semibold text-white">
            {achievement.title}
          </p>
          <p className="text-xs text-white/80">
            {achievement.description}
          </p>
        </div>
      </div>
    </ContainerShape>
  )
}

const LockedCard = ({ achievement }: { achievement: Achievement }) => {
  return (
    <ContainerShape {...{ cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 }}>
      <div className="p-3 md:p-4 flex flex-col gap-2.5">
        <InnerShape {...{ cornerDiagonal: 14, cornerRadius: 6, strokeWidth: 1 }}>
          <div className="h-[120px] md:h-[162px] rounded-xl bg-[#93641F] flex items-center justify-center">
            <img src={achievement.image} className="h-18 object-contain px-4 opacity-50" />
          </div>
        </InnerShape>

        <div className='flex flex-col items-start gap-1'>
           <div className="rounded" style={{ border: '1px solid #FFF085'}}>
            <div className="px-2 py-0.5 bg-[#F0B10080]">
              <p className="text-xs font-semibold text-white">
                {achievement.category}
              </p>
            </div>
          </div>
          <p className="text-sm font-semibold text-white/70">
            {achievement.title}
          </p>
          <p className="text-xs text-white/70">
            {achievement.description}
          </p>
        </div>
      </div>
    </ContainerShape>
  )
}
